import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import DashboardCard from "./dashboard-card";
import CircularProgress from "@mui/material/CircularProgress";

const ResponsiveGridLayout = WidthProvider(Responsive);

const EditWidgetModal = ({
  isOpen,
  onClose,
  onSave,
  widgetCard,
  layouts,
  layoutSize,
  handleLayoutChange,
}) => {
  const [tempLayout, setTempLayout] = useState(null);
  const [loadingSave, setLoadingSave] = useState(false);

  const handleTempLayoutChange = (newLayout) => {
    setTempLayout(newLayout);
  };

  const handleSave = async () => {
    setLoadingSave(true);

    try {
      if (tempLayout) {
        await handleLayoutChange(tempLayout);
      }
      onSave();
    } catch (error) {
      console.error("Error saving widget:", error);
    } finally {
      setLoadingSave(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          m: 0,
          height: "100vh",
          maxHeight: "100vh",
          borderRadius: 0,
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" component="div">
          Edit Card Layout
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 0,
          bgcolor: "background.default",
          overflow: "visible",
          height: "calc(100vh - 120px)",
        }}
      >
        <ResponsiveGridLayout
          className="layout"
          layouts={{
            lg: layouts,
            md: layouts,
            sm: layouts,
            xs: layouts,
            xxs: layouts,
          }}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 16, md: 10, sm: 10, xs: 6, xxs: 6 }}
          rowHeight={8}
          compactType="vertical"
          preventCollision={false}
          isDraggable={false}
          isResizable={false}
          margin={[8, 8]}
          useCSSTransforms={false}
          draggableCancel=".non-draggable"
          transitionDuration={0}
          animate={false}
          autoSize={false}
          style={{
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
            WebkitTapHighlightColor: "transparent",
            touchAction: "manipulation",
            height: "100%",
            width: "100%",
          }}
        >
          <div
            key={widgetCard?.id}
            style={{
              position: "relative",
              height: "100%",
              transition: "all 0.3s ease",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                height: "100%",
                width: "100%",
                padding: "4px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                borderRadius: "16px",
                bgcolor: "background.card",
                boxSizing: "border-box",
                overflow: "visible",
                zIndex: 1000,
                "& > *": {
                  overflow: "visible",
                },
              }}
            >
              <DashboardCard
                card={widgetCard}
                onLayoutChange={handleTempLayoutChange}
                drag={true}
                layoutSize={layoutSize}
              />
            </Paper>
          </div>
        </ResponsiveGridLayout>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loadingSave}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            textTransform: "none",
          }}
        >
          Save
          {loadingSave && (
            <CircularProgress
              size={20}
              sx={{
                color: "inherit",
              }}
            />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditWidgetModal;
